import {Controller} from '@hotwired/stimulus';

export default class extends Controller {

    static targets = ['timeSlotModal', 'begin', 'ende', 'compulsory', 'timeForm',
        'confirmationModal', 'confirmationForm', 'confirmationModalTitle', 'confirmationModalQuestion']

    TIME_GRID_CONFIG = {
        START_HOUR: 6,
        END_HOUR: 19,
        INTERVALS_PER_HOUR: 12,
        INTERVAL_MINUTES: 5,
        MATERIALIZE_DELAY: 500,
        UPDATE_DEBOUNCE: 300
    };

    DAYS_OF_WEEK = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];

    connect() {
        const form = document.getElementById('survey-step-form-modal') || document.getElementById('survey-step-form')
        if (!form) return;

        // take field "Bemerkungen" and extract the name
        this.form_data_array_name = form.querySelector('textarea').getAttribute('name').split("[")[0];

        const step_class = Array.from(form.classList).find(c => c.startsWith('step-'));
        const step = Number(step_class?.split('-')[1]);

        if (step === 6) {
            this.initializeTimeTable(true)
            this.handleEscape = this.handleEscape.bind(this);
        } else if (step === 7) {
            this.initializeTimeTable(false)
            this.handleEscape = this.handleEscape.bind(this);
        } else {
            throw Error("not implemented")
        }
    }

    disconnect() {
        // Clean up listener if controller is disconnected
        document.removeEventListener('keydown', this.handleEscape);
    }

    /**
     * @param {boolean} compulsory which is true for step6 "Sperrzeit" and which is false for step7 "Zeitwünsche"
     */
    initializeTimeTable(compulsory) {
        this.compulsory = compulsory;
        this.initializeDragState();
        // Stundenraster
        this.addTimeTableCellListener();
        // Text der ausgewählten Sperrzeiten
        this.updateTextOfSelections(this.compulsory, 0)
    }

    // =======================================================
    // ===== private methods
    // =======================================================

    initializeDragState() {
        this.dragStart = null;
        this.dragMode = null; // null, 'select', or 'deselect'
    }

    addTimeTableCellListener() {
        const timeTable = document.querySelector('.time-table');
        if (!timeTable) return;
        this.setupDragHandlers(timeTable);
        this.setupCellInteractions();
    }

    setupDragHandlers(timeTable) {
        // Stop dragging on mouse up
        document.addEventListener('mouseup', () => {
            if (this.isDragging()) {
                this.dragStart = null;
                this.dragMode = null;
            }
        });

        // Prevent text selection during drag
        timeTable.addEventListener('selectstart', (e) => {
            if (this.isDragging()) {
                e.preventDefault();
            }
        });
    }

    setupCellInteractions() {
        document.querySelectorAll('.interval-visual').forEach(cell => {
            cell.addEventListener('mousedown', (event) => this.onCellMouseDown(event));
            cell.addEventListener('mouseenter', (event) => this.onCellMouseEnter(event));
        });
    }

    onCellMouseDown(event) {
        event.preventDefault();
        const target = event.currentTarget;

        if (this.isTargetLocked(target)) {
            return;
        }

        const column = Number(target.dataset.column);
        const row = Number(target.dataset.row);

        this.startDragging(column, row, target);
        this.toggle60MinutesSlot(column, row, this.compulsory);
    }

    onCellMouseEnter(event) {
        if (!this.isDragging()) return;

        const target = event.currentTarget;

        if (this.isTargetLocked(target)) {
            return;
        }

        const column = Number(target.dataset.column);
        const row = Number(target.dataset.row);

        this.handleDragSelection(column, row);
    }

    startDragging(column, row, element) {
        this.dragStart = {column, row};
        if (element instanceof Element) {
            this.dragMode = element.classList.contains('checked') ? 'deselect' : 'select';
        }
    }

    isDragging() {
        return this.dragMode !== null;
    }

    isTargetLocked(target) {
        return target instanceof Element && target.classList.contains('locked');
    }


    handleDragSelection(column, row) {
        // Calculate rectangle bounds
        const minColumn = Math.min(this.dragStart.column, column);
        const maxColumn = Math.max(this.dragStart.column, column);
        const minRow = Math.floor(Math.min(this.dragStart.row, row) / 12) * 12;
        const maxRow = Math.floor(Math.max(this.dragStart.row, row) / 12) * 12 + 11;

        // Process cells within selection bounds
        document.querySelectorAll('.interval-visual').forEach(cell => {
            const col = Number(cell.dataset.column);
            const row = Math.floor(Number(cell.dataset.row) / 12) * 12;

            // Check if cell is within bounds
            if (col < minColumn || col > maxColumn || row < minRow || row > maxRow) {
                return; // Skip cells outside selection
            }

            // Toggle based on drag mode and current state
            if (row % 12 === 0) this.toggle60MinutesSlot(col, row);
        });
    }

    toggle60MinutesSlot(column, rowIdx) {
        const startRow = 12 * Math.floor(rowIdx / 12)
        const endRow = startRow + 11

        for (let row = startRow; row <= endRow; row++) {
            const cell = this.getCell(column, row, this.compulsory);

            if (this.isTargetLocked(cell)) continue;

            if (this.dragMode === 'select') {
                cell.classList.add('checked');
            } else {
                cell.classList.remove('checked');
            }
        }

        this.updateTextOfSelections(this.compulsory)
    }

    getCell(column, row, compulsory) {
        const suffix = compulsory ? '_c' : ''
        return document.getElementById(`interval_${column}_${row}${suffix}`);
    }

    handleModalTimeSelection(value, compulsory) {
        const [day, times] = value.split(' ');
        const [start, end] = times ? times.split('-') : ['6.00', '19.00'];
        const [startHour, startMinute] = start.split('.');
        const [endHour, endMinute] = end.split('.');

        this.column = this.DAYS_OF_WEEK.indexOf(day)

        this.initializeTimeSelectors(
            Number(startHour) * 60 + Number(startMinute),
            Number(endHour) * 60 + Number(endMinute)
        );

        if (this.compulsory && this.hasCompulsoryTarget) {
            this.compulsoryTarget.value = compulsory
        }

        const {START_HOUR, INTERVALS_PER_HOUR, INTERVAL_MINUTES} = this.TIME_GRID_CONFIG
        const rangeStart = (startHour - START_HOUR) * INTERVALS_PER_HOUR + Math.floor(startMinute / INTERVAL_MINUTES);
        const rangeEnd = (endHour - START_HOUR) * INTERVALS_PER_HOUR + Math.floor(endMinute / INTERVAL_MINUTES);
        this.selectedRange = {rangeStart, rangeEnd}

        // workaround materialize - stimulus: values from static targets = [begin, ende, …] are not applied
        this.reInitializeMaterializeCss()

        // Show the modal
        if (this.hasTimeSlotModalTarget) {
            this.timeSlotModalTarget.classList.remove('hidden');
            // Get the Materialize modal instance and disable ESC
            this.disableEscapeKeyMaterializeCssModal();
            // Add ESC key listener
            document.addEventListener('keydown', this.handleEscape);
        }
    }

    disableEscapeKeyMaterializeCssModal() {
        const materializeModal = document.querySelector('.modal.open');
        if (materializeModal) {
            const instance = M.Modal.getInstance(materializeModal);
            if (instance) {
                instance.options.dismissible = false;
            }
        }
    }

    initializeTimeSelectors(begin, end) {
        const formatTime = (totalMinutes) => {
            const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
            const minutes = (totalMinutes % 60).toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        };

        if (this.hasBeginTarget) {
            this.beginTarget.value = formatTime(begin);
        }
        if (this.hasEndeTarget) {
            this.endeTarget.value = formatTime(end);
        }
    }


    closeConfirmationModal(event) {
        event.preventDefault();
        this._closeModal(this.confirmationModalTarget, this.hasConfirmationModalTarget);
    }

    closeTimeSlotModal(event) {
        event.preventDefault();
        this._closeModal(this.timeSlotModalTarget, this.hasTimeSlotModalTarget);
    }

    _closeModal(modalTarget, hasTarget) {
        if (hasTarget) {
            modalTarget.classList.add('hidden');
        }

        // Remove ESC key listener
        document.removeEventListener('keydown', this.handleEscape);

        // Re-enable ESC for Materialize modal
        const materializeModal = document.querySelector('.modal.open');
        if (materializeModal) {
            const instance = M.Modal.getInstance(materializeModal);
            if (instance) {
                instance.options.dismissible = true;
            }
        }
    }

    submitSelection(event) {
        event.preventDefault();

        // Check if form is valid using native validation
        if (!this.timeFormTarget.checkValidity()) {
            this.timeFormTarget.reportValidity();
            return;
        }

        if (!this.selectedRange) {
            console.error('No current cells selected');
            this.closeTimeSlotModal(event);
            return;
        }

        const timeRange = this.getSelectedTimeRange();
        if (!timeRange) {
            this.closeTimeSlotModal(event);
            return;
        }

        const compulsory = this.getCompulsory()

        if (compulsory === null) {
            this.closeTimeSlotModal(event);
            return;
        }

        this.clearCurrentSelection(compulsory);
        this.applyNewTimeSelection(timeRange, compulsory);
        this.updateTextOfSelections(compulsory);
        this.closeTimeSlotModal(event);
    }

    getCompulsory() {
        if (!this.hasCompulsoryTarget) {
            return null;
        }

        const v = parseInt(this.compulsoryTarget?.value ?? -1);

        if (v === -1) {
            return null
        }

        return v === 1
    }

    getSelectedTimeRange() {
        const [beginHour, beginMinute] = (this.beginTarget?.value ?? "00:00").split(':').map(Number);
        const [endHour, endMinute ] =  (this.endeTarget?.value ?? "00:00").split(':').map(Number);

        return {beginHour, beginMinute, endHour, endMinute};
    }

    clearCurrentSelection(compulsory) {
        for (let row = this.selectedRange.rangeStart; row < this.selectedRange.rangeEnd; row++) {
            this.getCell(this.column, row, compulsory)?.classList.remove('checked');
            this.getCell(this.column, row, compulsory)?.classList.remove('locked');
        }
    }

    applyNewTimeSelection({beginHour, beginMinute, endHour, endMinute}, compulsory) {
        const {START_HOUR, INTERVALS_PER_HOUR, INTERVAL_MINUTES} = this.TIME_GRID_CONFIG;

        let row = INTERVALS_PER_HOUR * (beginHour - START_HOUR) + Math.floor(beginMinute / INTERVAL_MINUTES);
        let endRow = INTERVALS_PER_HOUR * (endHour - START_HOUR) + Math.floor(endMinute / INTERVAL_MINUTES);
        let cell = this.getCell(this.column, row, compulsory);

        while (cell && cell.dataset.row < endRow) {
            if (!this.isTargetLocked(cell)) {
                cell.classList.add('checked');
                if (compulsory) {
                    const otherCell = this.getCell(this.column, row, !compulsory);
                    otherCell?.classList.remove('checked')
                    otherCell?.classList.add('locked')
                }
            }

            cell = this.getCell(this.column, ++row, compulsory);
        }
    }

    deleteSelection(event, target) {
        event.preventDefault();

        const submitBtnValue = event.submitter.value;

        if (submitBtnValue === "yes") {
            const { column_index, start_row, end_row, compulsory } = this.selectedTimeSlotData;

            this.column = parseInt(column_index);
            this.selectedRange = {
                rangeStart: parseInt(start_row),
                rangeEnd: parseInt(end_row) + 1
            };

            this.clearCurrentSelection(compulsory === '1')
            this.updateTextOfSelections()
        }

        this.closeConfirmationModal(event);
        this.selectedTimeSlotData = null;
    }

    updateTextOfSelections(compulsory = null, debounce = this.TIME_GRID_CONFIG.UPDATE_DEBOUNCE) {
        if (compulsory === null) {
            this.updateTextOfSelections(true, debounce);
            this.updateTextOfSelections(false, debounce + 20);
            return
        }

        const timeoutKey = compulsory ? 'updateTimeoutTextOfSelections1' : 'updateTimeoutTextOfSelections2';

        // Cancel any pending update
        clearTimeout(this[timeoutKey]);

        // Schedule the update
        this[timeoutKey] = setTimeout(() => {
            this.clearSelectionDisplay(compulsory);
            this.renderTimeSlotSelections(compulsory);
        }, debounce);
    }

    clearSelectionDisplay(compulsory) {
        const selector = compulsory ? '.compulsory' : ':not(.compulsory)'

        document.querySelectorAll(`.selected-texts .day${selector}`).forEach(element => {
            element.classList.add('hidden');
        });

        document.querySelectorAll(`.selected-texts .times-container${selector}`).forEach(container => {
            container.classList.add('hidden');
            container.innerHTML = '';
        });

        if (this.compulsory === null) {
            const hasAddButtons = document.querySelectorAll(`.selected-texts .day${selector} > button`).length > 0
            document.querySelectorAll(`.selected-texts .day${selector}, .selected-texts .times-container${selector}`).forEach(element => {
                element.classList.remove('hidden');

                if (element.classList.contains('day') && !hasAddButtons) {
                    const addButton = this.createEditButton(element.dataset.column, -1, element.dataset.day, compulsory, 'add');
                    element.appendChild(addButton);
                }
            });
        }
    }

    renderTimeSlotSelections(compulsory) {
        let hasSelections = false;

        // processTimeSlotColumn from Monday to Friday
        for (let columnIndex = 0; columnIndex <= 4; columnIndex++) {
            let row = 0;
            let cell = this.getCell(columnIndex, row, compulsory);

            while (cell) {
                // Skip unchecked rows (not selected)
                while (cell && !this.isChecked(cell)) {
                    row++;
                    cell = this.getCell(columnIndex, row, compulsory);
                }

                if (!cell) break; // no more selected

                // Found start of checked sequence
                const startRow = row;

                // Find end of checked sequence
                while (cell && this.isChecked(cell)) {
                    row++;
                    cell = this.getCell(columnIndex, row, compulsory);
                }

                const endRow = row - 1;
                this.appendTimeSlotElement(columnIndex, startRow, endRow, compulsory);
                hasSelections = true;
            }

        }

        if (hasSelections) {
            document.querySelector('.selected-texts')?.classList.remove('hidden');
        }
    }

    isChecked = (cell) => {
        return cell instanceof Element && cell.classList.contains('checked');
    }

    appendTimeSlotElement(columnIndex, startRow, endRow, compulsory) {
        // Show day label
        const selector = compulsory ? '.compulsory' : ':not(.compulsory)'
        document
            .querySelectorAll(`.selected-texts div${selector}[data-column="${columnIndex}"]`)
            .forEach(c => c.classList.remove('hidden'))

        const container = document.querySelector(
            `.selected-texts div.times-container${selector}[data-column="${columnIndex}"]`
        )

        if (!container) return;

        const timeSlotDiv = this.createTimeSlotElement(columnIndex, startRow, endRow, compulsory);
        container.appendChild(timeSlotDiv);
    }

    createTimeSlotElement(columnIndex, startRow, endRow, compulsory) {
        const div = document.createElement('div');
        div.classList.add('times');

        const {text, value} = this.getTimeRangeOfSelection(columnIndex, startRow, endRow);

        const textSpan = document.createElement('span');
        textSpan.textContent = text;
        div.appendChild(textSpan);

        const editButton = this.createEditButton(columnIndex, startRow, value, compulsory);
        div.appendChild(editButton);

        const deleteButton = this.createDeleteButton(columnIndex, startRow, endRow, value, compulsory);
        div.appendChild(deleteButton);

        const hiddenInput = this.createHiddenInputValue(value);
        div.appendChild(hiddenInput);

        return div;
    }

    createHiddenInputValue(value) {
        const input = document.createElement('input');
        const field = this.compulsory ? 'sperrzeit' : 'zeitwunsch'

        input.type = 'hidden';
        input.name = `${this.form_data_array_name}[${field}][]`;
        input.value = value

        return input
    }

    createEditButton(columnIndex, startRow, value, compulsory, icon = 'edit') {
        const button = document.createElement('a');

        button.classList.add('btn-floating', 'blue-text', 'text-darken-3', 'btn-delete', 'slplaner-col-navbar-button');
        button.dataset.start_row = startRow;
        button.dataset.compulsory = compulsory ? '1' : '0';
        button.dataset.value = value;
        button.dataset.column_index = columnIndex;
        button.innerHTML = `<i class="material-icons">${icon}</i>`;

        button.onclick = (event) => {
            event.preventDefault();
            let target = event.target;

            while (target && !target.dataset.start_row) {
                target = target.parentElement;
            }

            if (target) {
                this.handleModalTimeSelection(target.dataset.value, target.dataset?.compulsory);
            }
        };

        return button;
    }

    createDeleteButton(columnIndex, startRow, endRow, value, compulsory, icon = 'delete') {
        const button = document.createElement('a');

        button.classList.add('btn-floating', 'blue-text', 'text-darken-3', 'btn-delete', 'slplaner-col-navbar-button');
        button.dataset.start_row = startRow;
        button.dataset.end_row = endRow;
        button.dataset.value = value;
        button.dataset.compulsory = compulsory ? '1' : '0';
        button.dataset.column_index = columnIndex;
        button.innerHTML = `<i class="material-icons">${icon}</i>`;

        button.onclick = (event) => {
            event.preventDefault();
            let target = event.target;

            while (target && !target.dataset.start_row) {
                target = target.parentElement;
            }

            if (target) {
                // Show the modal
                if (this.hasConfirmationModalTarget) {
                    this.selectedTimeSlotData = target.dataset;
                    if (this.hasConfirmationModalQuestionTarget) {
                        this.confirmationModalQuestionTarget.innerHTML = `<p>${target.dataset.value}</p><p>Zeitfenster löschen?</p>`
                    }

                    this.confirmationModalTarget.classList.remove('hidden');
                    // Get the Materialize modal instance and disable ESC
                    this.disableEscapeKeyMaterializeCssModal();
                    // Add ESC key listener
                    document.addEventListener('keydown', this.handleEscape);
                }
            }
        };

        return button;
    }

    getTimeRangeOfSelection = (columnIndex, startRow, endRow) => {
        const {START_HOUR, INTERVALS_PER_HOUR, INTERVAL_MINUTES} = this.TIME_GRID_CONFIG;

        const startHour = String(START_HOUR + Math.floor(startRow / INTERVALS_PER_HOUR))
            .padStart(2, '0');
        const startMinute = String(INTERVAL_MINUTES * (startRow % INTERVALS_PER_HOUR))
            .padStart(2, '0');

        const endHour = String(START_HOUR + Math.floor((endRow + 1) / INTERVALS_PER_HOUR))
            .padStart(2, '0');
        const endMinute = String(INTERVAL_MINUTES * ((endRow + 1) % INTERVALS_PER_HOUR))
            .padStart(2, '0');

        const text = `${startHour}.${startMinute} – ${endHour}.${endMinute}`;
        const dayOfWeek = this.DAYS_OF_WEEK[columnIndex]
        const value = `${dayOfWeek} ${startHour}.${startMinute}-${endHour}.${endMinute}`;

        return {text, value};
    }

    reInitializeMaterializeCss() {
        const container = document.getElementById('edit-step-modal') || document.getElementById('view_step')

        // Stimulus controller 'materializecss-init' / materializecss_init_controller.js
        // noinspection JSUnresolvedReference
        this.application
            .getControllerForElementAndIdentifier(this.element, 'materializecss-init')
            .reInitializeMaterializeCss(container);
    }


    // Check if ESC was pressed and modal-in-modal is open
    handleEscape(event) {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        event.stopPropagation();

        if (this.hasConfirmationModalTarget && !this.confirmationModalTarget.classList.contains('hidden')) {
            this.closeConfirmationModal(event);
        } else if (this.hasTimeSlotModalTarget && !this.timeSlotModalTarget.classList.contains('hidden')) {
            this.closeTimeSlotModal(event);
        }
    }
}